import React from 'react';
import { Row, Col, Card, CardBody, Spinner } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import KeyFigureStatic from '@/common/charts/KeyFigureStatic';
import { FinancialMetricsDto } from '@/services/analytics/analytics-types';
import { ApexOptions } from 'apexcharts';

/**
 * Generate distinct darker pastel colors for charts based on the number of items
 * Uses HSL color space with moderate saturation and medium lightness for darker pastel effect
 */
const generateDistinctColors = (count: number): string[] => {
  const colors: string[] = [];
  const hueStep = 360 / count;

  for (let i = 0; i < count; i++) {
    const hue = (i * hueStep) % 360;
    // Darker pastel colors: moderate saturation (50-70%) and medium lightness (55-65%)
    const saturation = 50 + (i % 3) * 10; // 50%, 60%, 70%
    const lightness = 55 + (i % 2) * 5; // 55%, 60%
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }

  return colors;
};

interface FinancialMetricsSectionProps {
  data?: FinancialMetricsDto;
  isLoading: boolean;
}

const FinancialMetricsSection: React.FC<FinancialMetricsSectionProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="p-5 text-center">
        <Spinner color="primary" />
        <p className="mt-2">Loading financial metrics...</p>
      </div>
    );
  }

  if (!data) {
    return <div className="p-5 text-center">No data available</div>;
  }

  const createMetricItem = (
    value: number,
    label: string,
    icon = 'bx bx-dollar-circle',
    prefix = '$',
    decimals = 2,
  ) => ({
    id: 1,
    cardColor: 'primary',
    label: label,
    badge: '',
    badgeClass: '',
    counter: value,
    bgcolor: 'secondary',
    icon,
    decimals,
    prefix,
    suffix: '',
    separator: ',',
    link: '',
  });

  // Prepare chart data for LTV by cohort
  const ltvByCohort = data.ltvByCohort || [];
  const ltvColors = generateDistinctColors(ltvByCohort.length);
  const ltvChartOptions = {
    chart: {
      type: 'bar' as const,
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: ltvByCohort.map((item) => item.cohort),
    },
    yaxis: {
      title: {
        text: 'LTV ($)',
      },
    },
    colors: ltvColors,
    tooltip: {
      y: {
        formatter: (val: number) =>
          `$${val.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`,
      },
    },
  };

  const ltvChartSeries = [
    {
      name: 'LTV',
      data: ltvByCohort.map((item) => item.ltv),
    },
  ];

  // Prepare chart data for deposits by channel
  const depositsByChannel = data.depositsByChannel || [];
  const channelColors = generateDistinctColors(depositsByChannel.length);
  const channelChartOptions: ApexOptions = {
    chart: {
      type: 'donut' as const,
      height: 300,
    },
    labels: depositsByChannel.map((item) => item.channel),
    colors: channelColors,
    legend: {
      position: 'bottom' as const,
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(2)}%`,
    },
    tooltip: {
      y: {
        formatter: (val: number) =>
          `$${val.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`,
      },
    },
  };

  const channelChartSeries = depositsByChannel.map((item) => item.amount);

  // Prepare revenue breakdown chart
  const revenueBreakdownOptions = {
    chart: {
      type: 'donut' as const,
      height: 300,
    },
    labels: ['Rake Share (USDT)', 'Rakeback (PHNM)', 'Revenue Share', 'Net Revenue'],
    colors: ['#f46a6a', '#f1b44c', '#50a5f1', '#34c38f'],
    legend: {
      position: 'bottom' as const,
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(2)}%`,
    },
    tooltip: {
      y: {
        formatter: (val: number) =>
          `$${val.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`,
      },
    },
  };

  const revenueBreakdownSeries = [
    data.rakeSharePaidUsdt,
    data.rakebackPaidPhnm,
    data.revenueShareAmount,
    data.netRevenue,
  ];

  return (
    <>
      {/* Revenue Metrics - Primary Focus */}
      <Row className="gy-3 mb-4">
        <Col lg={12}>
          <h4 className="mb-3">Revenue Metrics</h4>
        </Col>
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.totalRakeGenerated, 'Total Rake Generated', 'bx bx-dollar-circle')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.rakeSharePaidUsdt, 'Rake Share Paid (USDT)', 'bx bx-share-alt')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.rakebackPaidPhnm, 'Rakeback Paid (PHNM)', 'bx bx-coin-stack')}
        />
        <KeyFigureStatic columnProps={{ lg: 3 }} item={createMetricItem(data.netRake, 'Net Rake', 'bx bx-wallet')} />
      </Row>

      <Row className="gy-3 mb-4">
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.revenueShareAmount, 'Revenue Share (50%)', 'bx bx-group')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.tokenRedemptionCosts, 'Token Redemptions', 'bx bx-transfer')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.netRevenue, 'Net Revenue', 'bx bx-trending-up')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.arpu, 'ARPU (Avg Rake Per User)', 'bx bx-user')}
        />
      </Row>

      {/* Pending Rake Share Metrics */}
      {data.weeklyRakeShareMetrics && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card>
              <CardBody>
                <h5 className="card-title mb-3">Pending Weekly Rake Share</h5>
                <Row>
                  <Col md={3}>
                    <div className="text-center">
                      <h6 className="text-muted">Total in USDT</h6>
                      <h4 className="text-">
                        $
                        {data.weeklyRakeShareMetrics.totalInUSDT.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}
                      </h4>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h6 className="text-muted">Pending USDT</h6>
                      <h4 className="text-info">
                        $
                        {data.weeklyRakeShareMetrics.totalPendingUsdt.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}
                      </h4>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h6 className="text-muted">Pending PHNM</h6>
                      <h4 className="text-primary">
                        {data.weeklyRakeShareMetrics.totalPendingPhnm.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}
                      </h4>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h6 className="text-muted">PHNM Payout %</h6>
                      <h4 className="text-warning">
                        {(data.weeklyRakeShareMetrics.phnmPayoutPercentage * 100).toFixed(2)}%
                      </h4>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* User Metrics */}
      <Row className="gy-3 mb-4">
        <Col lg={12}>
          <h4 className="mb-3">User Metrics</h4>
        </Col>
        <KeyFigureStatic
          columnProps={{ lg: 6 }}
          item={createMetricItem(data.arpdau, 'ARPDAU (Daily Avg Rake)', 'bx bx-calendar')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 6 }}
          item={createMetricItem(data.bonusCosts, 'Bonus Costs', 'bx bx-gift')}
        />
      </Row>

      {/* Deposit/Withdrawal Metrics */}
      <Row className="gy-3 mb-4">
        <Col lg={12}>
          <h4 className="mb-3">Deposit & Withdrawal Metrics</h4>
        </Col>
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.grossDeposits, 'Gross Deposits', 'bx bx-import')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.totalWithdrawals, 'Total Withdrawals', 'bx bx-export')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 3 }}
          item={createMetricItem(data.depositWithdrawalRatio, 'Deposit/Withdrawal Ratio', 'bx bx-refresh', '', 2)}
        />
      </Row>

      {/* Charts Row */}
      <Row className="mb-4">
        {/* Revenue Breakdown Chart */}
        <Col lg={6}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-3">Revenue Breakdown</h5>
              <ReactApexChart
                options={revenueBreakdownOptions}
                series={revenueBreakdownSeries}
                type="donut"
                height={300}
              />
            </CardBody>
          </Card>
        </Col>

        {/* LTV Chart */}
        <Col lg={6}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-3">LTV by Cohort</h5>
              <ReactApexChart options={ltvChartOptions} series={ltvChartSeries} type="bar" height={300} />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Deposits by Channel */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-3">Deposits by Channel</h5>
              <Row>
                <Col lg={6}>
                  <ReactApexChart
                    options={channelChartOptions}
                    series={channelChartSeries}
                    type="donut"
                    height="100%"
                  />
                </Col>
                <Col lg={6}>
                  <div className="mt-3">
                    <table className="table-sm table">
                      <thead>
                        <tr>
                          <th>Channel</th>
                          <th className="text-end">Amount</th>
                          <th className="text-end">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {depositsByChannel.map((channel, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div
                                  className="me-2 rounded"
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: channelColors[index],
                                  }}
                                ></div>
                                {channel.channel}
                              </div>
                            </td>
                            <td className="text-end">${channel.amount.toLocaleString()}</td>
                            <td className="text-end">{channel.count.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default FinancialMetricsSection;
